import { Controller } from "@hotwired/stimulus"
import {
  TimeSeriesGestures,
  TimeSeriesViewport,
  ViewportDirection,
  ViewportLoadResult,
  ViewportRange
} from "../charts/time_series_viewport"

interface MarketCandle {
  timestamp: string
  open: number
  high: number
  low: number
  close: number
  volume: number
}

interface CandleResponse {
  status: string
  retry_after_ms?: number
  error_message?: string
  candles?: MarketCandle[]
  starts_on?: string
  ends_on?: string
}

export default class MarketCandlesController extends Controller {
  static targets = ["canvas", "tooltip", "announcement", "earlier", "later", "reset", "status"]
  static values = {
    candles: Array,
    bucket: String,
    symbol: String,
    dataUrl: String,
    startsOn: String,
    endsOn: String,
    coverageStartsOn: String,
    coverageEndsOn: String
  }

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly tooltipTarget: HTMLDivElement
  declare readonly announcementTarget: HTMLParagraphElement
  declare readonly earlierTarget: HTMLButtonElement
  declare readonly laterTarget: HTMLButtonElement
  declare readonly resetTarget: HTMLButtonElement
  declare readonly statusTarget: HTMLParagraphElement
  declare readonly candlesValue: MarketCandle[]
  declare readonly bucketValue: string
  declare readonly symbolValue: string
  declare readonly dataUrlValue: string
  declare readonly startsOnValue: string
  declare readonly endsOnValue: string
  declare readonly coverageStartsOnValue: string
  declare readonly coverageEndsOnValue: string

  private resizeObserver?: ResizeObserver
  private gestures?: TimeSeriesGestures
  private viewport?: TimeSeriesViewport<MarketCandle>
  private activeIndex?: number
  private geometry?: { left: number, top: number, width: number, height: number }
  private abortController = new AbortController()
  private connected = false

  connect() {
    this.connected = true
    this.abortController = new AbortController()
    this.viewport = new TimeSeriesViewport({
      points: this.candlesValue,
      position: (candle) => Date.parse(candle.timestamp),
      viewport: this.dateRange(this.startsOnValue, this.endsOnValue),
      loaded: this.dateRange(this.startsOnValue, this.endsOnValue),
      coverage: this.dateRange(this.coverageStartsOnValue, this.coverageEndsOnValue),
      minimumSpan: this.minimumSpan(),
      loadRange: (range, direction) => this.loadRange(range, direction),
      onChange: () => {
        this.activeIndex = undefined
        this.tooltipTarget.hidden = true
        this.announcementTarget.textContent = ""
        this.updateNavigation()
        this.draw()
      },
      onLoadingChange: (direction, error) => this.updateLoading(direction, error)
    })
    this.gestures = new TimeSeriesGestures(this.canvasTarget, {
      onPan: (fraction) => this.viewport?.panByFraction(fraction),
      onZoom: (factor, anchor) => this.viewport?.zoomByFactor(factor, anchor),
      onSelect: (startsFraction, endsFraction) => this.zoomToSelection(startsFraction, endsFraction),
      onHover: (event) => this.inspect(event),
      onLeave: () => this.clear()
    })
    this.gestures.connect()
    this.resizeObserver = new ResizeObserver(() => this.draw())
    this.resizeObserver.observe(this.canvasTarget)
    this.updateNavigation()
    this.draw()
  }

  disconnect() {
    this.connected = false
    this.abortController.abort()
    this.gestures?.disconnect()
    this.resizeObserver?.disconnect()
  }

  earlier() {
    this.viewport?.page("earlier")
  }

  later() {
    this.viewport?.page("later")
  }

  reset() {
    this.viewport?.reset()
  }

  inspect(event: PointerEvent) {
    const candles = this.visibleCandles
    if (!this.geometry || candles.length === 0 || !this.viewport) return

    const bounds = this.canvasTarget.getBoundingClientRect()
    const position = Math.max(this.geometry.left, Math.min(event.clientX - bounds.left, this.geometry.left + this.geometry.width))
    const ratio = (position - this.geometry.left) / this.geometry.width
    const range = this.viewport.range
    const targetTime = range.startsAt + ratio * (range.endsAt - range.startsAt)
    let nearest = 0
    candles.forEach((candle, index) => {
      if (Math.abs(Date.parse(candle.timestamp) - targetTime) < Math.abs(Date.parse(candles[nearest].timestamp) - targetTime)) nearest = index
    })
    this.select(nearest, event.clientX - bounds.left, event.clientY - bounds.top)
  }

  clear() {
    this.activeIndex = undefined
    this.tooltipTarget.hidden = true
    this.draw()
  }

  focus() {
    if (this.activeIndex === undefined) this.select(this.visibleCandles.length - 1)
  }

  navigate(event: KeyboardEvent) {
    if (["+", "="].includes(event.key)) {
      event.preventDefault()
      this.viewport?.zoomByFactor(0.72)
      return
    }
    if (["-", "_"].includes(event.key)) {
      event.preventDefault()
      this.viewport?.zoomByFactor(1.38)
      return
    }
    if (event.key === "0") {
      event.preventDefault()
      this.viewport?.reset()
      return
    }
    if (!event.key.match(/^Arrow(Left|Right)$/)) return

    event.preventDefault()
    if (event.shiftKey) {
      this.viewport?.page(event.key === "ArrowLeft" ? "earlier" : "later")
      return
    }
    const offset = event.key === "ArrowLeft" ? -1 : 1
    this.select((this.activeIndex ?? this.visibleCandles.length - 1) + offset)
  }

  private get visibleCandles() {
    return this.viewport?.visiblePoints || []
  }

  private select(index: number, pointerX?: number, pointerY?: number) {
    const candles = this.visibleCandles
    if (candles.length === 0) return

    this.activeIndex = Math.max(0, Math.min(index, candles.length - 1))
    const candle = candles[this.activeIndex]
    const description = `${this.formatTime(candle.timestamp)}. Open ${this.price(candle.open)}, high ${this.price(candle.high)}, low ${this.price(candle.low)}, close ${this.price(candle.close)}, volume ${this.volume(candle.volume)}.`
    this.announcementTarget.textContent = description
    this.tooltipTarget.innerHTML = `<strong>${this.formatTime(candle.timestamp)}</strong><span>O ${this.price(candle.open)} · H ${this.price(candle.high)} · L ${this.price(candle.low)} · C ${this.price(candle.close)}</span><small>Volume ${this.volume(candle.volume)}</small>`
    this.tooltipTarget.hidden = false
    this.positionTooltip(pointerX, pointerY)
    this.draw()
  }

  private positionTooltip(pointerX?: number, pointerY?: number) {
    const candles = this.visibleCandles
    if (!this.geometry || this.activeIndex === undefined || !this.viewport) return

    const candleTime = Date.parse(candles[this.activeIndex].timestamp)
    const range = this.viewport.range
    const x = pointerX ?? this.geometry.left + ((candleTime - range.startsAt) / Math.max(range.endsAt - range.startsAt, 1)) * this.geometry.width
    const y = pointerY ?? this.geometry.top + 18
    const chartWidth = this.canvasTarget.getBoundingClientRect().width
    this.tooltipTarget.style.left = `${Math.min(Math.max(x + 14, 10), chartWidth - 230)}px`
    this.tooltipTarget.style.top = `${Math.max(y - 24, 8)}px`
  }

  private draw() {
    const canvas = this.canvasTarget
    const candles = this.visibleCandles
    const bounds = canvas.getBoundingClientRect()
    if (bounds.width === 0 || bounds.height === 0 || candles.length === 0 || !this.viewport) return

    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.floor(bounds.width * ratio)
    canvas.height = Math.floor(bounds.height * ratio)
    const context = canvas.getContext("2d")
    if (!context) return
    context.scale(ratio, ratio)

    const geometry = { left: 68, top: 18, width: Math.max(bounds.width - 88, 1), height: Math.max(bounds.height - 54, 1) }
    this.geometry = geometry
    const lows = candles.map((candle) => candle.low)
    const highs = candles.map((candle) => candle.high)
    const minimum = Math.min(...lows)
    const maximum = Math.max(...highs)
    const padding = Math.max((maximum - minimum) * 0.06, maximum * 0.001, 0.0001)
    const low = minimum - padding
    const high = maximum + padding
    const viewportRange = this.viewport.range
    const y = (value: number) => geometry.top + ((high - value) / (high - low)) * geometry.height
    const x = (candle: MarketCandle) => geometry.left + ((Date.parse(candle.timestamp) - viewportRange.startsAt) / Math.max(viewportRange.endsAt - viewportRange.startsAt, 1)) * geometry.width

    context.clearRect(0, 0, bounds.width, bounds.height)
    context.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace"
    context.textBaseline = "middle"
    context.strokeStyle = "rgba(23, 25, 20, .09)"
    context.fillStyle = "#6d7168"
    context.lineWidth = 1
    for (let index = 0; index <= 4; index += 1) {
      const lineY = geometry.top + (geometry.height * index / 4)
      context.beginPath()
      context.moveTo(geometry.left, lineY)
      context.lineTo(geometry.left + geometry.width, lineY)
      context.stroke()
      const value = high - ((high - low) * index / 4)
      context.textAlign = "right"
      context.fillText(this.price(value), geometry.left - 9, lineY)
    }

    const step = Math.max(1, Math.ceil(candles.length / 5))
    for (let index = 0; index < candles.length; index += step) {
      context.textAlign = index === 0 ? "left" : "center"
      context.fillText(this.axisTime(candles[index].timestamp), x(candles[index]), geometry.top + geometry.height + 22)
    }

    const candleWidth = Math.max(1, Math.min(12, (geometry.width / Math.max(candles.length, 1)) * 0.66))
    candles.forEach((candle) => {
      const candleX = x(candle)
      const rising = candle.close >= candle.open
      context.strokeStyle = rising ? "#18764d" : "#b83d35"
      context.fillStyle = rising ? "#18764d" : "#b83d35"
      context.beginPath()
      context.moveTo(candleX, y(candle.high))
      context.lineTo(candleX, y(candle.low))
      context.stroke()
      const bodyTop = Math.min(y(candle.open), y(candle.close))
      const bodyHeight = Math.max(Math.abs(y(candle.open) - y(candle.close)), 1)
      context.fillRect(candleX - candleWidth / 2, bodyTop, candleWidth, bodyHeight)
    })

    if (this.activeIndex !== undefined && candles[this.activeIndex]) {
      const activeX = x(candles[this.activeIndex])
      context.strokeStyle = "rgba(23, 25, 20, .48)"
      context.setLineDash([4, 4])
      context.beginPath()
      context.moveTo(activeX, geometry.top)
      context.lineTo(activeX, geometry.top + geometry.height)
      context.stroke()
      context.setLineDash([])
    }
  }

  private async loadRange(range: ViewportRange, direction: ViewportDirection): Promise<ViewportLoadResult<MarketCandle>> {
    const url = new URL(this.dataUrlValue, window.location.origin)
    url.searchParams.set("starts_on", this.isoDate(range.startsAt))
    url.searchParams.set("ends_on", this.isoDate(range.endsAt))

    while (this.connected) {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })
      const payload = await response.json() as CandleResponse
      if (response.ok && payload.status === "ready" && payload.candles && payload.starts_on && payload.ends_on) {
        return {
          points: payload.candles,
          ...this.dateRange(payload.starts_on, payload.ends_on)
        }
      }
      if (response.status !== 202) throw new Error(payload.error_message || `Could not load ${direction} chart data`)
      await this.delay(payload.retry_after_ms || 2_000)
    }
    throw new DOMException("Chart disconnected", "AbortError")
  }

  private updateNavigation() {
    if (!this.viewport) return
    this.earlierTarget.disabled = !this.viewport.canPan("earlier")
    this.laterTarget.disabled = !this.viewport.canPan("later")
    this.resetTarget.disabled = !this.viewport.canReset
    if (this.viewport.loading) return

    const range = this.viewport.range
    const label = `${this.shortDate(range.startsAt)} – ${this.shortDate(range.endsAt)}`
    const headingRange = this.element.closest(".market-candle-panel")?.querySelector<HTMLElement>(".panel__heading .eyebrow")
    if (headingRange) headingRange.textContent = label
    this.canvasTarget.setAttribute(
      "aria-label",
      `Candlestick chart for ${this.symbolValue} from ${label}. Scroll vertically to zoom, drag to select a range, and scroll horizontally to move through time. Use plus or minus to zoom, zero to reset, arrow keys to inspect candles, and Shift plus an arrow key to move one page.`
    )
    this.statusTarget.textContent = `${label} · wheel to zoom · drag to select`
  }

  private updateLoading(direction?: ViewportDirection, error?: Error) {
    if (error) {
      if (error.name !== "AbortError") this.statusTarget.textContent = error.message
      return
    }
    if (direction) {
      this.statusTarget.textContent = `Loading ${direction} data…`
      return
    }
    this.updateNavigation()
  }

  private dateRange(startsOn: string, endsOn: string) {
    return {
      startsAt: Date.parse(`${startsOn}T00:00:00.000Z`),
      endsAt: Date.parse(`${endsOn}T23:59:59.999Z`)
    }
  }

  private zoomToSelection(startsFraction: number, endsFraction: number) {
    if (!this.viewport) return
    const range = this.viewport.range
    const span = range.endsAt - range.startsAt
    this.viewport.zoomTo({
      startsAt: range.startsAt + span * startsFraction,
      endsAt: range.startsAt + span * endsFraction
    })
  }

  private minimumSpan() {
    const positions = this.candlesValue.map((candle) => Date.parse(candle.timestamp)).sort((left, right) => left - right)
    const intervals = positions.slice(1).map((position, index) => position - positions[index]).filter((interval) => interval > 0)
    if (intervals.length === 0) return 60_000
    intervals.sort((left, right) => left - right)
    return intervals[Math.floor(intervals.length / 2)] * 8
  }

  private isoDate(timestamp: number) {
    return new Date(timestamp).toISOString().slice(0, 10)
  }

  private shortDate(timestamp: number) {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" }).format(new Date(timestamp))
  }

  private delay(milliseconds: number) {
    return new Promise<void>((resolve, reject) => {
      const aborted = () => {
        window.clearTimeout(timer)
        reject(new DOMException("Request aborted", "AbortError"))
      }
      const timer = window.setTimeout(() => {
        this.abortController.signal.removeEventListener("abort", aborted)
        resolve()
      }, milliseconds)
      this.abortController.signal.addEventListener("abort", aborted, { once: true })
    })
  }

  private formatTime(timestamp: string) {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      ...(this.intraday() ? { timeStyle: "short", timeZone: "UTC" } : {})
    }).format(new Date(timestamp))
  }

  private axisTime(timestamp: string) {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      ...(this.intraday() ? { hour: "2-digit", minute: "2-digit", timeZone: "UTC" } : {})
    }).format(new Date(timestamp))
  }

  private intraday() {
    return ["1m", "5m", "15m", "1h"].includes(this.bucketValue)
  }

  private price(value: number) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: value < 1 ? 5 : 2 }).format(value)
  }

  private volume(value: number) {
    return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value)
  }
}
